// Reine Logik der DataTable (ohne React) – getestet in dataTableUtils.test.js

export const BREAKPOINTS = { compact: 640, full: 1024 };

/** Darstellung je nach verfügbarer Breite des Containers (nicht des Fensters) */
export function getLayout(width) {
  if (!width || width < BREAKPOINTS.compact) return 'cards';
  if (width < BREAKPOINTS.full) return 'compact';
  return 'full';
}

const priorityOf = (column) => column.priority ?? 2;

/** Spalten, die in der Tabelle direkt sichtbar sind; der Rest klappt pro Zeile auf */
export function splitColumns(columns, layout) {
  // Priorität 4: Nebeninfos, die auch am PC nur in der aufklappbaren Detailzeile stehen
  const maxPriority = layout === 'full' ? 3 : layout === 'compact' ? 2 : 1;
  return {
    visible: columns.filter((c) => priorityOf(c) <= maxPriority),
    hidden: columns.filter((c) => priorityOf(c) > maxPriority)
  };
}

/** Rollen der Spalten in der Kartenansicht (Handy) */
export function cardColumns(columns) {
  const title = columns.find((c) => c.primary) || columns[0];
  const subtitle = columns.find((c) => c.secondary && c !== title);
  const details = columns.filter((c) => c !== title && c !== subtitle && !c.hideInCard);
  return { title, subtitle, details };
}

export function getValue(row, column, kind = 'value') {
  if (kind === 'search' && column.searchValue) return column.searchValue(row);
  if (kind === 'sort' && column.sortValue) return column.sortValue(row);
  return column.accessor ? column.accessor(row) : row?.[column.key];
}

const normalize = (text) => String(text ?? '').toLocaleLowerCase('de').normalize('NFKD').replace(/[̀-ͯ]/g, '');

/** Alle Suchbegriffe müssen in irgendeiner durchsuchbaren Spalte vorkommen (ä = a, Groß/klein egal) */
export function filterRows(rows, query, columns) {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return rows;
  const searchable = columns.filter((c) => c.searchable !== false);
  return rows.filter((row) => {
    const haystack = searchable.map((c) => normalize(getValue(row, c, 'search'))).join(' ');
    return terms.every((term) => haystack.includes(term));
  });
}

const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

/** Stabile Sortierung; leere Werte stehen immer am Ende */
export function sortRows(rows, sort, columns) {
  if (!sort?.key) return rows;
  const column = columns.find((c) => c.key === sort.key);
  if (!column) return rows;
  const factor = sort.direction === 'desc' ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index, value: getValue(row, column, 'sort') }))
    .sort((a, b) => {
      const aEmpty = a.value == null || a.value === '';
      const bEmpty = b.value == null || b.value === '';
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? a.index - b.index : aEmpty ? 1 : -1;
      let result;
      if (typeof a.value === 'number' && typeof b.value === 'number') result = a.value - b.value;
      else if (a.value instanceof Date && b.value instanceof Date) result = a.value - b.value;
      else result = collator.compare(String(a.value).trim(), String(b.value).trim());
      return result * factor || a.index - b.index;
    })
    .map((entry) => entry.row);
}

/** Nächster Sortierzustand beim Klick auf eine Spalte: aufsteigend → absteigend → aus */
export function nextSort(sort, key) {
  if (sort?.key !== key) return { key, direction: 'asc' };
  if (sort.direction === 'asc') return { key, direction: 'desc' };
  return null;
}

export function paginate(rows, page, pageSize) {
  if (!pageSize || pageSize <= 0) return { items: rows, page: 1, pageCount: 1 };
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  return { items: rows.slice((current - 1) * pageSize, current * pageSize), page: current, pageCount };
}
